/**
 * The staff directory: `/admin/crm/staff`, and the only client-side description
 * of what that route answers with.
 *
 * It lives in its own file because it had TWO descriptions before. `projects.ts`
 * declared the full row and `support.ts` declared `{ id, name, email }`, and
 * both compiled: a narrower interface over the same JSON is not a type error,
 * it is a promise to ignore the rest. That stayed harmless only while the route
 * itself filtered deactivated people out, so the fields the narrow copy dropped
 * did not exist yet. The moment the route started returning everybody, the
 * support-side pickers began offering people who had left the company - and
 * their type did not even carry the flag that could have stopped it. Nothing in
 * the compiler could have caught that, because the two copies were each
 * internally consistent. One endpoint therefore gets one type here, and the two
 * old modules re-export it so no caller had to move.
 *
 * The list deliberately INCLUDES people who have left. Their names have to keep
 * resolving on last year's time entries and on assignee chips written before
 * they went: an id with nothing behind it renders as a bug, not as somebody who
 * has gone. So the fetch never filters - deciding who may be given NEW work is
 * the picker's job (`active === false` sorted last and marked, see
 * `staffOptionLabel` and the directory hook in
 * `crm/sections/_shared/staff.ts`), and a filter hidden in the fetch would take
 * that decision away from every screen at once.
 */
import { api } from "./api.js";

/**
 * One person who can be an assignee, a project member or the writer of a time
 * entry. It comes from the PLATFORM database - the Membership rows on the host
 * site, joined to User - because the CRM has no user table of its own. It stores
 * ids and trusts somebody else to know the names.
 *
 * `active: false` is how a screen tells a person who has left from one who is
 * still here. It is the whole reason this row is wider than a name lookup, so
 * do not narrow it back down at a call site.
 */
export interface StaffUser {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  active: boolean;
}

/**
 * Guarded by `view:crm`, and deliberately not served from `/admin/users`: that
 * route is behind `manage:users.global`, a right a project operator does not
 * carry. "Who can I assign this to" is not the same question as "who may
 * administer accounts", and pointing the picker at the admin route would quietly
 * make the second right a prerequisite for doing the first job.
 */
export const getStaff = () => api.get<{ rows: StaffUser[] }>("/admin/crm/staff");

/**
 * How a person is written into a plain option list, and how one who has left is
 * marked there.
 *
 * `UserPicker` can draw its own rows, so it mutes a deactivated person and puts
 * the word beside the avatar. The three remaining pickers cannot: a native
 * `<option>` and the CRM's `MenuField` both take a string and nothing else, so
 * the only place the fact fits is inside the label. Without it a person who left
 * in March looks exactly like a colleague at the next desk, and the ticket goes
 * to them.
 *
 * The word is passed in rather than translated here on purpose - this module is
 * React-free and is imported by data code that has no `useT` to call, while
 * every caller already holds one. Falls back to the email the same way
 * `staffLabel` does: a membership whose user never typed a name is still
 * somebody the reader can identify.
 */
export function staffOptionLabel(person: StaffUser, inactiveWord: string): string {
  const name = person.name.trim() || person.email.trim();
  return person.active ? name : `${name} (${inactiveWord})`;
}
